/**
 * CustomFiltroCalendarios — a barra "o que exibir" da agenda (ficha #27, Fase 2).
 *
 * É a caixinha que se marca nos calendários do Google: das pastas que EU posso
 * ver, quais eu quero ver agora. Pedido dele em 02/09 — "cada um vai ter a sua
 * página de acordo com os critérios que a gente setar".
 *
 * ─── DUAS COISAS QUE SE CONFUNDEM ──────────────────────────────────────────
 *
 *   QUEM PODE VER    decidido no backend (`_calendario_acessos`)
 *   O QUE EU EXIBO   esta barra
 *
 * Desmarcar aqui é deixar de desenhar, nunca deixar de ter acesso — e marcar
 * não dá acesso a nada: a listagem já vem filtrada pelo que a pessoa pode ver.
 *
 * ─── SEM COMPONENTE NOVO, SEM COR NOVA ─────────────────────────────────────
 *
 * A mesma forma do filtro "Mostrar:" que já existe três linhas acima na tela:
 * rótulo pequeno + `btn-group btn-group-sm` com `btn-theme`/`btn-outline-theme`.
 * Reusar a forma que está ali é o que faz a barra nova não parecer enxertada —
 * e não há um pixel de CSS nosso neste arquivo.
 *
 * A preferência é gravada no servidor a cada toque, e de propósito: ela
 * acompanha a pessoa do computador da loja para o celular. O toque é otimista
 * (a tela responde na hora); se o servidor recusar, o estado volta.
 */
import { useCallback, useEffect, useState } from 'react';

import { api } from '@/lib/api';

export interface Calendario {
  id: number;
  calendario: string;
  nome: string;
  tipo: string;
  papel: string;
}

interface Preferencias {
  calendarios_exibidos: string[] | null;
  fontes_exibidas: string[] | null;
  visao_preferida: string | null;
  mostrar_vencidas: boolean;
}

interface Props {
  /** Chamado sempre que a marcação muda — `null` quer dizer "todos". */
  aoMudar: (exibidos: string[] | null) => void;
}

export function CustomFiltroCalendarios({ aoMudar }: Props) {
  const [calendarios, setCalendarios] = useState<Calendario[]>([]);
  const [exibidos, setExibidos] = useState<string[] | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [cals, prefs] = await Promise.all([
          api.get<Calendario[]>('/agenda/calendarios'),
          api.get<Preferencias>('/agenda/me/preferencias'),
        ]);
        if (!vivo) return;
        setCalendarios(cals);
        setExibidos(prefs.calendarios_exibidos);
        aoMudar(prefs.calendarios_exibidos);
      } catch {
        // A barra é conveniência: se as portas falharem, a agenda continua
        // mostrando tudo o que a pessoa pode ver, que é o padrão.
        if (vivo) setCalendarios([]);
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // `aoMudar` vem do pai e não deve reexecutar a carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alternar = useCallback(
    async (codigo: string) => {
      const todos = calendarios.map((c) => c.calendario);
      const atuais = exibidos ?? todos;
      const novos = atuais.includes(codigo)
        ? atuais.filter((c) => c !== codigo)
        : [...atuais, codigo];

      // Marcar tudo volta a ser "todos" (`null`) em vez de uma lista que
      // envelhece: pasta criada amanhã apareceria desmarcada sem ninguém
      // entender por quê.
      const valor = novos.length === todos.length ? null : novos;

      const anterior = exibidos;
      setExibidos(valor);
      aoMudar(valor);
      try {
        await api.put('/agenda/me/preferencias', { calendarios_exibidos: valor });
      } catch {
        setExibidos(anterior);
        aoMudar(anterior);
      }
    },
    [calendarios, exibidos, aoMudar],
  );

  // Uma pasta só não é escolha — não se desenha uma barra para dizer que a
  // agenda tem uma agenda.
  if (carregando || calendarios.length < 2) return null;

  const todos = calendarios.map((c) => c.calendario);
  const marcados = exibidos ?? todos;

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <span className="small text-secondary me-1">
        <i className="bi bi-calendar2-check me-1"></i> Exibir:
      </span>
      <div className="btn-group btn-group-sm" role="group">
        {calendarios.map((c) => {
          const on = marcados.includes(c.calendario);
          return (
            <button
              key={c.calendario}
              type="button"
              className={`btn ${on ? 'btn-theme' : 'btn-outline-theme'}`}
              onClick={() => alternar(c.calendario)}
              title={on ? `Esconder ${c.nome}` : `Mostrar ${c.nome}`}
            >
              {c.nome}
            </button>
          );
        })}
      </div>
      {marcados.length < todos.length && (
        <span className="small text-secondary">
          <i className="bi bi-info-circle me-1"></i>
          Escondido da vista — nada foi apagado.
        </span>
      )}
    </div>
  );
}

export default CustomFiltroCalendarios;
